import { useEffect } from "react";
import { required, TextInput, regex, email, Button } from "react-admin";
import { Grid } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { settings } from "app";
import dataProvider from "server/dataProvider";
import { accountRequests } from "api/accountApi";

const clearBulgarianSearchData = setValue => {
  setValue("userId", null, { shouldDirty: true, shouldValidate: true });
  setValue("info.firstName", "");
  setValue("info.secondName", "");
  setValue("info.lastName", "");
  setValue("info.contactPhone", "");
  setValue("email", "");
  setValue("info.gender", null);
};

const clearForeignerSearchData = setValue => {
  setValue("userId", null, { shouldDirty: true, shouldValidate: true });
  setValue("info.firstName", "");
  setValue("info.secondName", "");
  setValue("info.lastName", "");
  setValue("info.firstNameEn", "");
  setValue("info.secondNameEn", "");
  setValue("info.lastNameEn", "");
  setValue("info.gender", null);
  setValue("info.residenceCountryId", null);
};

const UserSearchInput = ({ isForeigner, onSearched }) => {
  const personId = useWatch({ name: "info.personId" });
  const emailValue = useWatch({ name: "email" });
  const { setValue, trigger } = useFormContext();

  const searchUserByPersonId = async () => {
    const isValid = await trigger("info.personId");
    if (!isValid) {
      return;
    }

    onSearched(false);
    try {
      const { data } = await dataProvider.customRequest(accountRequests.getUsersInfoSearch({ personId }), null, "GET");
      if (data) {
        setValue("userId", data?.userId || null, { shouldDirty: true, shouldValidate: true });
        setValue("info.firstName", data?.firstName || "");
        setValue("info.secondName", data?.secondName || "");
        setValue("info.lastName", data?.lastName || "");
        setValue("info.contactPhone", data?.contactPhone || "");
        setValue("email", data?.email || "");
        setValue("info.gender", data?.gender);
      } else {
        clearBulgarianSearchData(setValue);
      }
    } catch (error) {
      console.error(error);
      clearBulgarianSearchData(setValue);
    } finally {
      onSearched(true);
    }
  };

  const searchUserByEmail = async () => {
    const isValid = await trigger("email");
    if (!isValid) {
      return;
    }

    onSearched(false);
    try {
      const { data } = await dataProvider.customRequest(
        accountRequests.getUsersInfoSearch({ email: emailValue }),
        null,
        "GET",
      );
      if (data) {
        setValue("userId", data?.userId || null, { shouldDirty: true, shouldValidate: true });
        setValue("info.firstName", data?.firstName || "");
        setValue("info.secondName", data?.secondName || "");
        setValue("info.lastName", data?.lastName || "");
        setValue("info.firstNameEn", data?.firstNameEn || "");
        setValue("info.secondNameEn", data?.secondNameEn || "");
        setValue("info.lastNameEn", data?.lastNameEn || "");
        setValue("info.gender", data?.gender);
        setValue("info.residenceCountryId", data?.residenceCountryId || null);
      } else {
        clearForeignerSearchData(setValue);
      }
    } catch (error) {
      console.error(error);
      clearForeignerSearchData(setValue);
    } finally {
      onSearched(true);
    }
  };

  useEffect(() => {
    if (isForeigner) return;
    clearBulgarianSearchData(setValue);
    onSearched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  useEffect(() => {
    if (!isForeigner) return;
    clearForeignerSearchData(setValue);
    onSearched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue]);

  return (
    <Grid
      container
      spacing={4}
      sx={settings.style.gridContainer}
    >
      <Grid
        item
        xs={12}
        md={4}
      >
        {isForeigner ? (
          <TextInput
            label="Имейл"
            source="email"
            validate={[required(), email()]}
            fullWidth
          />
        ) : (
          <TextInput
            label="ЕГН"
            source="info.personId"
            validate={[required(), regex(/^[0-9]{10}$/, "Моля въведете валидно ЕГН")]}
            fullWidth
          />
        )}
      </Grid>
      <Grid
        item
        xs={12}
        md={2}
      >
        <Button
          label="Провери"
          onClick={isForeigner ? searchUserByEmail : searchUserByPersonId}
          size="large"
          variant="contained"
          disabled={isForeigner ? !emailValue : !personId}
          sx={{ mt: 1.5 }}
        />
      </Grid>
    </Grid>
  );
};

export default UserSearchInput;
